/**
 * Entrata Chatbot Block
 *
 * Injection block that loads the Entrata contact-button / chat widget from the
 * embed snippet stored on the property. The snippet is a blob of <script> tags
 * (i18n libs + a contact_btn_controller bootstrap that calls ppcb.render). We
 * parse it, clone the scripts, and append them to document head exactly once
 * per tab — behaving like a static <script> pasted into index.html.
 *
 * Why singleton + never-tear-down: Entrata paints its launcher directly into
 * the DOM (outside our tracking) and re-running the bootstrap calls
 * ppcb.render again, duplicating the widget. So we inject once and leave it
 * alone for the tab's lifetime, which survives StrictMode and SPA navigation
 * remounts. Subsequent edits to the embed snippet won't reflect until a hard
 * refresh — an acceptable trade for not duplicating the widget. This mirrors
 * the rentcafe-chatbot block.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const INJECTED_NODE_ATTRIBUTE = 'data-entrata-chatbot-node';

type EntrataChatbotState = {
  nodes: Node[];
  value: string | null;
};

function normalizeValue(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function createScriptNode(originalScript: HTMLScriptElement) {
  const script = document.createElement('script');

  Array.from(originalScript.attributes).forEach(({ name, value }) => {
    script.setAttribute(name, value);
  });

  script.textContent = originalScript.textContent;
  script.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');
  return script;
}

/**
 * Append a node to <head>, resolving only once it has "run". External
 * `<script src>` elements resolve on load/error; everything else (inline
 * scripts, which execute synchronously on append, and non-script nodes)
 * resolves immediately. Awaiting this between nodes serializes execution so
 * the scripts run in document order — exactly like parser-inserted tags in a
 * static <head>. Entrata's snippet depends on this: i18n.min.js defines the
 * global `__` translation helper that entrata-ui.min.js and the contact-button
 * bootstrap both reference, and dynamically-inserted scripts are async by
 * default (download-order execution), which otherwise throws "__ is not
 * defined".
 */
function appendHeadNode(node: Node) {
  if (node instanceof HTMLScriptElement && node.src) {
    return new Promise<void>(resolve => {
      node.addEventListener('load', () => resolve(), { once: true });
      node.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(node);
    });
  }

  document.head.appendChild(node);
  return Promise.resolve();
}

function createHeadNodes(embedScript: string) {
  const normalized = normalizeValue(embedScript);
  if (!normalized) return [];

  const template = document.createElement('template');
  template.innerHTML = normalized;

  return Array.from(template.content.childNodes).flatMap(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      return text ? [document.createTextNode(text)] : [];
    }

    if (!(node instanceof HTMLElement)) return [];
    if (node instanceof HTMLScriptElement) return [createScriptNode(node)];

    const clonedNode = node.cloneNode(true);
    if (clonedNode instanceof HTMLElement) {
      clonedNode.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');
    }

    return [clonedNode];
  });
}

function getEntrataChatbotState() {
  const windowWithState = window as Window & {
    __entrataChatbotState?: EntrataChatbotState;
  };

  // Single tab-wide instance. Once we've injected the vendor scripts, we
  // remember it here so subsequent mounts (StrictMode, SPA navigation, etc.)
  // skip re-injection — Entrata paints its widget directly in <body>, and
  // re-running the bootstrap (ppcb.render) duplicates the widget.
  if (windowWithState.__entrataChatbotState) {
    return windowWithState.__entrataChatbotState;
  }

  const state: EntrataChatbotState = {
    nodes: [],
    value: null,
  };

  windowWithState.__entrataChatbotState = state;
  return state;
}

function EntrataChatbotComponent(_props: BlockProps) {
  const embedScript = normalizeValue(
    useBlockToken('embed_script', {
      defaultField: 'custom_entrata_chatbot',
    }),
  );

  useEffect(() => {
    if (!embedScript) return;

    const state = getEntrataChatbotState();

    // Load the vendor scripts exactly once per tab and never tear them down.
    // Entrata creates its launcher DOM directly in <body> — outside our
    // tracking. Tearing down + re-injecting on remount or SPA navigation makes
    // the vendor paint a SECOND widget on top of the existing one (broken
    // styles + lost click events), since the original body DOM isn't ours to
    // clean up. The safe-and-simple answer is to behave like a script in
    // index.html: inject once, leave alone for the tab's lifetime. Subsequent
    // edits to the embed script won't reflect until a hard refresh — an
    // acceptable trade for not duplicating the widget.
    if (state.value) return;

    const nodes = createHeadNodes(embedScript);
    if (!nodes.length) return;

    // Mark injected synchronously so a concurrent remount can't kick off a
    // second sequential load before this one finishes.
    state.value = embedScript;
    state.nodes = nodes;

    // Load sequentially: each external script must finish executing before the
    // next node is appended, so i18n.min.js defines `__` before entrata-ui and
    // the contact-button bootstrap reference it. See appendHeadNode.
    void (async () => {
      for (const node of nodes) {
        await appendHeadNode(node);
      }
    })();
  }, [embedScript]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'EntrataChatbot',
  displayName: 'Entrata Chatbot',
  tokens: {
    embed_script: {
      label: 'Embed Script',
      type: 'text',
      defaultField: 'custom_entrata_chatbot',
      suggestedFields: ['custom_entrata_chatbot'],
      description: 'Entrata contact-button / chat embed script tags stored on the property.',
    },
  },
};

export const entrataChatbotBlock: BlockDefinition = {
  id: 'entrata-chatbot',
  name: 'Entrata Chatbot',
  description:
    'Entrata contact-button / chat widget injected from the property embed snippet. The floating launcher is the UI — clicking it opens the contact / chat flow.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: EntrataChatbotComponent,
  contentSchema,
  tags: ['entrata', 'chatbot', 'chat', 'contact', 'widget', 'ppcb'],
};
